import { createElement, getUiRoot, setText } from './dom';
import { setLabel } from './i18n';
import { createIcon } from './icons';
import { isChord } from '../controls/keymap';
import type { DiscoveryPlace } from '../discovery/types';
import type { Book } from '../discovery/book';
import { discoverySource } from './discoverySource';
import { createDiscoveryQuestions } from './discoveryQuestions';

function entry(parent: HTMLElement, place: DiscoveryPlace, found: boolean): HTMLElement {
  const root = createElement('li', found ? 'discovery-entry' : 'discovery-entry is-hidden', parent);
  root.dataset.discovery = place.id;
  root.dataset.found = String(found);
  const status = createElement('p', 'discovery-status', root);
  setText(status, found ? 'Found' : 'Not found yet');
  if (found) setText(createElement('h4', 'discovery-name', root), place.name);
  setText(createElement('p', 'discovery-text', root), found ? place.text : place.clue);
  if (found) discoverySource(root, place);
  return root;
}

function elements() {
  const launcher = createElement('button', 'player-context discovery-launcher', getUiRoot());
  launcher.type = 'button';
  launcher.hidden = true;
  setLabel(launcher, 'aria-label', 'Discovery book (K)');
  setLabel(launcher, 'title', 'Discovery book (K)');
  const icon = createIcon('book', 20);
  icon.setAttribute('aria-hidden', 'true');
  launcher.appendChild(icon);
  const count = createElement('span', 'discovery-count', launcher);
  const dialog = createElement('dialog', 'player-context discovery-book', getUiRoot());
  dialog.setAttribute('aria-labelledby', 'discovery-title');
  const heading = createElement('div', 'emote-heading', dialog);
  const title = createElement('h2', '', heading);
  title.id = 'discovery-title';
  setText(title, 'Discovery book');
  const close = createElement('button', 'emote-close', heading);
  close.type = 'button';
  setLabel(close, 'aria-label', 'Close');
  setLabel(close, 'title', 'Close');
  close.appendChild(createIcon('close', 16));
  const progress = createElement('div', 'discovery-progress', dialog);
  const bar = createElement('progress', '', progress);
  setLabel(bar, 'aria-label', 'Places found');
  const share = createElement('span', 'discovery-share', progress);
  const hint = createElement('p', 'discovery-hint', dialog);
  setText(hint, 'Walk up to a place, or row to it, to add it to your book.');
  const account = createElement('div', 'discovery-account', dialog);
  setText(
    createElement('p', 'discovery-account-text', account),
    'Sign in to keep the places you find in your book.'
  );
  const signIn = createElement('button', 'discovery-sign-in', account);
  signIn.type = 'button';
  setText(signIn, 'Sign in');
  const failing = createElement('p', 'discovery-failing', dialog);
  failing.setAttribute('role', 'alert');
  setText(failing, 'Not saved to your book yet. Trying again.');
  const practice = createElement('div', '', dialog);
  const pages = createElement('div', 'discovery-pages', dialog);
  const content = { hint, account, signIn, failing, practice, pages };
  return { launcher, count, dialog, close, bar, share, ...content };
}

export interface BookKeeping {
  /** The book belongs to a signed-in account and can keep what is found. */
  readonly signedIn: boolean;
  /** A finding is waiting to be saved again. */
  readonly failing: boolean;
}

/** The book of the city's places: what has been found, told, and what is still to find, as a
 *  riddle. `changed` hears it open and close, as the gesture picker's does. */
class DiscoveryBook {
  private readonly ui = elements();
  private drawn = '';

  constructor(
    private readonly book: () => Book,
    private readonly keeping: () => BookKeeping,
    private readonly available: () => boolean,
    private readonly changed: (open: boolean) => void,
    signIn: () => void
  ) {
    const { ui } = this;
    createDiscoveryQuestions(
      ui.practice,
      this.book().sections.flatMap((section) => section.entries.map((one) => one.place))
    );
    ui.signIn.addEventListener('click', () => {
      this.hide();
      signIn();
    });
    ui.launcher.addEventListener('click', () => {
      this.show();
    });
    ui.close.addEventListener('click', this.hide);
    ui.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.hide();
    });
    ui.dialog.addEventListener('click', this.outside);
    document.addEventListener('keydown', this.key, true);
  }

  get isOpen(): boolean {
    return this.ui.dialog.open;
  }

  show(place?: DiscoveryPlace): void {
    const { ui } = this;
    if (!this.available()) return;
    if (!ui.dialog.open) {
      if (document.pointerLockElement) document.exitPointerLock();
      ui.dialog.showModal();
      this.changed(true);
      this.drawn = '';
    }
    this.draw();
    const at = place ? ui.pages.querySelector<HTMLElement>(`[data-discovery="${place.id}"]`) : null;
    at?.scrollIntoView({ block: 'center' });
    ui.close.focus();
  }

  update(): void {
    const can = this.available();
    this.ui.launcher.hidden = !can;
    if (!can) this.hide();
    this.draw();
  }

  dispose(): void {
    this.hide();
    document.removeEventListener('keydown', this.key, true);
    this.ui.dialog.remove();
    this.ui.launcher.remove();
  }

  private draw(): void {
    const { ui } = this;
    const current = this.book();
    const keeping = this.keeping();
    ui.count.textContent = `${current.found} / ${current.total}`;
    ui.bar.max = current.total;
    ui.bar.value = current.found;
    ui.share.textContent = `${current.found} / ${current.total} · ${current.percent}%`;
    ui.hint.hidden = !keeping.signedIn;
    ui.account.hidden = keeping.signedIn;
    ui.failing.hidden = !keeping.failing;
    const drawn = `${String(keeping.signedIn)}:${String(current.found)}`;
    if (!ui.dialog.open || this.drawn === drawn) return;
    this.drawn = drawn;
    ui.pages.replaceChildren();
    for (const section of current.sections) {
      const page = createElement('section', 'discovery-page', ui.pages);
      const head = createElement('h3', 'discovery-theme', page);
      setText(head, section.title);
      createElement('span', 'discovery-theme-count', head).textContent =
        `${section.found} / ${section.entries.length}`;
      const list = createElement('ul', 'discovery-entries', page);
      for (const one of section.entries) entry(list, one.place, one.found);
    }
  }

  private readonly hide = (): void => {
    if (!this.ui.dialog.open) return;
    this.ui.dialog.close();
    this.changed(false);
    this.ui.launcher.focus({ preventScroll: true });
  };

  private readonly key = (event: KeyboardEvent): void => {
    if (event.repeat || isChord(event)) return;
    if (this.ui.dialog.open) {
      if (event.code !== 'Escape' && event.code !== 'KeyK') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.hide();
      return;
    }
    if (event.code !== 'KeyK') return;
    if (
      event.target instanceof HTMLElement &&
      event.target.closest('input, textarea, select, dialog, [role="dialog"]')
    )
      return;
    if (!this.available()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.show();
  };

  private readonly outside = (event: MouseEvent): void => {
    const { dialog } = this.ui;
    if (event.target !== dialog) return;
    const box = dialog.getBoundingClientRect();
    if (
      event.clientX < box.left ||
      event.clientX > box.right ||
      event.clientY < box.top ||
      event.clientY > box.bottom
    )
      this.hide();
  };
}

export function createDiscoveryBook(
  book: () => Book,
  keeping: () => BookKeeping,
  available: () => boolean,
  changed: (open: boolean) => void,
  signIn: () => void
) {
  return new DiscoveryBook(book, keeping, available, changed, signIn);
}
